import { useMemo } from 'react'

import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid2'
import MenuItem from '@mui/material/MenuItem'
import Skeleton from '@mui/material/Skeleton'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'

import type { AcceptanceLine } from '@server/domain/dossier-review'
import type { CertificateTiming, QueueCaseRow, SourceOutcome } from '@server/domain/operations-metrics'
import { INTEGRATION_STRATEGIES, SOURCES, SOURCE_KEYS, STRATEGY_LABEL } from '@server/domain/sources'

import CustomChip from '@core/components/mui/chip'
import CustomTextField from '@core/components/mui/text-field'
import AppLink from '@components/link'
import { TableDefault } from '@components/table-default'
import ExpiryQueueList from '@components/due-diligence/expiry-queue-list'
import KpiCard from '@components/due-diligence/kpi-card'
import ProgressRing from '@components/due-diligence/progress-ring'
import SuccessRateChart from '@components/due-diligence/success-rate-chart'
import type { ThemeColor } from '@core/types'

import { useTRPC } from '@/lib/trpc'
import { requireAdmin } from '@/lib/route-guards'
import { SUBJECT_TYPE, formatCents, formatDateTime, formatMinutes, formatPercent, pluralize } from '@/lib/due-diligence'

/** How often the panel asks again. Short enough to be a plantão, long enough
 * not to keep a scan over every certificate running back to back. */
const REFRESH_MS = 15_000

const PERIODS = [
  { days: 7, label: 'Últimos 7 dias' },
  { days: 30, label: 'Últimos 30 dias' },
  { days: 90, label: 'Últimos 90 dias' }
]

/** The filter lives in the URL, so a panel worth looking at can be sent. */
const searchSchema = z.object({
  dias: z.coerce.number().int().min(1).max(365).default(30),
  fonte: z.enum(SOURCE_KEYS).optional(),
  estrategia: z.enum(INTEGRATION_STRATEGIES).optional(),
  // Off is a deliberate state: reading a frozen number beats one that moves
  // under the cursor while someone copies it into a message.
  aoVivo: z.boolean().default(true)
})

type PanelSearch = z.infer<typeof searchSchema>

export const Route = createFileRoute('/_dashboard/admin/operations')({
  beforeLoad: requireAdmin,
  validateSearch: searchSchema,
  component: Operations
})

const rateColor = (rate: number | null): ThemeColor => {
  if (rate === null) return 'secondary'
  if (rate >= 0.95) return 'success'
  if (rate >= 0.8) return 'warning'

  return 'error'
}

const daysSince = (date: Date | string, now: number) =>
  Math.max(0, Math.floor((now - new Date(date).getTime()) / 86_400_000))

const queueColumn = createColumnHelper<QueueCaseRow>()
const timingColumn = createColumnHelper<CertificateTiming>()
const sourceColumn = createColumnHelper<SourceOutcome>()
const acceptanceColumn = createColumnHelper<AcceptanceLine>()

function Operations() {
  const trpc = useTRPC()
  const navigate = useNavigate({ from: Route.fullPath })
  const { dias, fonte, estrategia, aoVivo } = Route.useSearch()

  const { data, isPending, dataUpdatedAt } = useQuery(
    trpc.operations.panel.queryOptions(
      { days: dias, source: fonte, strategy: estrategia },
      { refetchInterval: aoVivo ? REFRESH_MS : false }
    )
  )

  const now = dataUpdatedAt || Date.now()

  const setSearch = (patch: Partial<PanelSearch>) => navigate({ search: current => ({ ...current, ...patch }) })

  const queueColumns = useMemo(
    () => [
      queueColumn.accessor('projectName', {
        header: 'Caso',
        cell: ({ row }) => (
          <div className='flex flex-col'>
            <Typography
              variant='body2'
              component={AppLink}
              href={`/diligences/${row.original.id}`}
              className='font-medium no-underline text-textPrimary hover:text-primary'
            >
              {row.original.projectName}
            </Typography>
            <Typography variant='caption' color='text.disabled'>
              {row.original.kind}
              {row.original.ownerName ? ` · ${row.original.ownerName}` : ''}
            </Typography>
          </div>
        )
      }),
      queueColumn.accessor('resolved', {
        header: 'Progresso',
        cell: ({ row }) => (
          <div className='flex items-center gap-3'>
            <ProgressRing value={row.original.resolved} total={row.original.certificates} size={40} />
            <Typography variant='caption' color='text.disabled' className='whitespace-nowrap'>
              {row.original.resolved} / {row.original.certificates}
            </Typography>
          </div>
        )
      }),
      queueColumn.accessor('running', {
        header: 'Em busca',
        cell: ({ row }) => (
          <Typography variant='body2' className='tabular-nums whitespace-nowrap'>
            {row.original.running} rodando · {row.original.queuedJobs} na fila
          </Typography>
        )
      }),
      queueColumn.accessor('awaiting', {
        header: 'Aguardando',
        cell: ({ getValue }) => (
          <Typography variant='body2' className='tabular-nums'>
            {getValue()}
          </Typography>
        )
      }),
      queueColumn.accessor('unconfirmed', {
        header: 'Vínculo não confirmado',
        cell: ({ getValue }) =>
          getValue() ? (
            // Homônimo é o erro caro: apontamento na pessoa errada trava um
            // negócio legítimo, e quem opera tem de ver a conta.
            <CustomChip round='true' size='small' variant='tonal' color='warning' label={getValue()} />
          ) : (
            <Typography variant='body2' color='text.disabled'>
              —
            </Typography>
          )
      }),
      queueColumn.accessor('blocked', {
        header: 'Precisa ação',
        cell: ({ getValue }) =>
          getValue() ? (
            <CustomChip round='true' size='small' variant='tonal' color='error' label={getValue()} />
          ) : (
            <Typography variant='body2' color='text.disabled'>
              —
            </Typography>
          )
      }),
      queueColumn.accessor('createdAt', {
        header: 'Idade',
        cell: ({ getValue }) => (
          <Typography variant='body2' className='whitespace-nowrap'>
            {pluralize(daysSince(getValue(), now), 'dia', 'dias')}
          </Typography>
        )
      }),
      queueColumn.accessor('lastActivityAt', {
        header: 'Última atividade',
        cell: ({ getValue }) => (
          <Typography variant='body2' color='text.secondary' className='whitespace-nowrap'>
            {getValue() ? formatDateTime(getValue()) : '—'}
          </Typography>
        )
      })
    ],
    [now]
  )

  const timingColumns = useMemo(
    () => [
      timingColumn.accessor('name', {
        header: 'Certidão',
        cell: ({ row }) => (
          <div className='flex flex-col'>
            <Typography variant='body2' className='font-medium'>
              {row.original.name}
            </Typography>
            <Typography variant='caption' color='text.disabled'>
              {SUBJECT_TYPE[row.original.subjectType].label} · {row.original.sourceLabel}
            </Typography>
          </div>
        )
      }),
      timingColumn.accessor('strategy', {
        header: 'Estratégia',
        cell: ({ getValue }) => (
          <CustomChip round='true' size='small' variant='tonal' color='secondary' label={STRATEGY_LABEL[getValue()]} />
        )
      }),
      timingColumn.accessor('samples', {
        header: 'Amostra',
        cell: ({ getValue }) => (
          <Typography variant='body2' className='tabular-nums'>
            {getValue()}
          </Typography>
        )
      }),
      timingColumn.accessor('averageCycleMinutes', {
        header: 'Ciclo',
        cell: ({ getValue }) => (
          <Typography variant='body2' className='whitespace-nowrap'>
            {formatMinutes(getValue())}
          </Typography>
        )
      }),
      timingColumn.accessor('averagePortalMinutes', {
        header: 'No portal',
        cell: ({ getValue }) => (
          <Typography variant='body2' color='text.secondary' className='whitespace-nowrap'>
            {formatMinutes(getValue())}
          </Typography>
        )
      })
    ],
    []
  )

  const sourceColumns = useMemo(
    () => [
      sourceColumn.accessor('label', {
        header: 'Fonte',
        cell: ({ row }) => (
          <div className='flex flex-col'>
            <Typography variant='body2' className='font-medium'>
              {row.original.label}
            </Typography>
            <Typography variant='caption' color='text.disabled'>
              {STRATEGY_LABEL[row.original.strategy]}
            </Typography>
          </div>
        )
      }),
      sourceColumn.accessor('attempts', {
        header: 'Tentativas',
        cell: ({ row }) => (
          <Typography variant='body2' className='tabular-nums whitespace-nowrap'>
            {row.original.attempts} · {row.original.failures} com falha
          </Typography>
        )
      }),
      sourceColumn.accessor('successRate', {
        header: 'Taxa',
        cell: ({ getValue }) => (
          <CustomChip
            round='true'
            size='small'
            variant='tonal'
            color={rateColor(getValue())}
            label={formatPercent(getValue())}
          />
        )
      })
    ],
    []
  )

  const acceptanceColumns = useMemo(
    () => [
      acceptanceColumn.accessor('projectName', {
        header: 'Caso',
        cell: ({ row }) => (
          <Typography
            variant='body2'
            component={AppLink}
            href={`/diligences/${row.original.diligenceId}`}
            className='font-medium no-underline text-textPrimary hover:text-primary'
          >
            {row.original.projectName}
          </Typography>
        )
      }),
      acceptanceColumn.accessor('openedAt', {
        header: 'Aberto em',
        cell: ({ getValue }) => (
          <Typography variant='body2' className='whitespace-nowrap'>
            {formatDateTime(getValue())}
          </Typography>
        )
      }),
      acceptanceColumn.accessor('acceptedAt', {
        header: '1º aceite',
        cell: ({ getValue }) => {
          const value = getValue()

          return (
            <Typography variant='body2' className='whitespace-nowrap' color={value ? 'text.primary' : 'text.disabled'}>
              {value ? formatDateTime(value) : 'ainda não aceito'}
            </Typography>
          )
        }
      }),
      acceptanceColumn.accessor('minutesToAccept', {
        header: 't₄',
        cell: ({ getValue }) => (
          <Typography variant='body2' className='tabular-nums whitespace-nowrap'>
            {formatMinutes(getValue())}
          </Typography>
        )
      }),
      acceptanceColumn.accessor('refusals', {
        header: 'Recusas antes',
        cell: ({ getValue }) => (
          <Typography variant='body2' className='tabular-nums'>
            {getValue()}
          </Typography>
        )
      }),
      acceptanceColumn.accessor('reworkNeeded', {
        header: 'Retrabalho',
        cell: ({ getValue }) =>
          getValue() ? (
            <CustomChip round='true' size='small' variant='tonal' color='warning' label='sim' />
          ) : (
            <Typography variant='body2' color='text.disabled'>
              não
            </Typography>
          )
      })
    ],
    []
  )

  const acceptanceTable = useReactTable({
    data: data?.acceptanceCases ?? [],
    columns: acceptanceColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } }
  })

  const queueTable = useReactTable({
    data: data?.queue.cases ?? [],
    columns: queueColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } }
  })

  const timingTable = useReactTable({
    data: data?.timings.certificates ?? [],
    columns: timingColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } }
  })

  const sourceTable = useReactTable({
    data: data?.outcomes.bySource ?? [],
    columns: sourceColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 8 } }
  })

  const strategyData = useMemo(
    () =>
      (data?.outcomes.byStrategy ?? []).map(strategy => ({
        key: strategy.strategy,
        label: strategy.label,
        successRate: strategy.successRate,
        attempts: strategy.attempts,
        successes: strategy.successes,
        failures: strategy.failures,
        caption: `${pluralize(strategy.catalogueItems, 'item', 'itens')} do catálogo · falha típica: ${strategy.typicalFailure}`
      })),
    [data]
  )

  const expensive = (data?.costs.cases ?? []).slice(0, 6)

  return (
    <>
      <div className='flex flex-wrap items-start justify-between gap-4 mbe-6'>
        <div>
          <Typography variant='h4'>Operação</Typography>
          <Typography color='text.secondary'>
            O que está parado, quanto tempo cada certidão leva e o que está falhando — por estratégia de integração, que
            é quem decide o conserto.
          </Typography>
        </div>
        <div className='flex items-center gap-4'>
          <Typography variant='caption' color='text.disabled' className='whitespace-nowrap'>
            {dataUpdatedAt ? `Atualizado às ${new Date(dataUpdatedAt).toLocaleTimeString('pt-BR')}` : 'Carregando…'}
          </Typography>
          <FormControlLabel
            control={<Switch checked={aoVivo} onChange={event => setSearch({ aoVivo: event.target.checked })} />}
            label='Tempo real'
          />
        </div>
      </div>

      <div className='flex flex-wrap items-center gap-4 mbe-6'>
        <CustomTextField
          select
          size='small'
          className='is-[200px]'
          value={dias}
          onChange={event => setSearch({ dias: Number(event.target.value) })}
        >
          {PERIODS.map(period => (
            <MenuItem key={period.days} value={period.days}>
              {period.label}
            </MenuItem>
          ))}
        </CustomTextField>
        <CustomTextField
          select
          size='small'
          className='is-[220px]'
          value={fonte ?? 'all'}
          onChange={event => setSearch({ fonte: SOURCE_KEYS.find(key => key === event.target.value) })}
        >
          <MenuItem value='all'>Todas as fontes</MenuItem>
          {SOURCE_KEYS.map(key => (
            <MenuItem key={key} value={key}>
              {SOURCES[key].label}
            </MenuItem>
          ))}
        </CustomTextField>
        <CustomTextField
          select
          size='small'
          className='is-[220px]'
          value={estrategia ?? 'all'}
          onChange={event =>
            setSearch({ estrategia: INTEGRATION_STRATEGIES.find(item => item === event.target.value) })
          }
        >
          <MenuItem value='all'>Todas as estratégias</MenuItem>
          {INTEGRATION_STRATEGIES.map(strategy => (
            <MenuItem key={strategy} value={strategy}>
              {STRATEGY_LABEL[strategy]}
            </MenuItem>
          ))}
        </CustomTextField>
      </div>

      <Grid container spacing={6}>
        {isPending
          ? [0, 1, 2, 3].map(index => (
              <Grid key={index} size={{ xs: 12, sm: 6, lg: 3 }}>
                <Skeleton variant='rounded' height={148} />
              </Grid>
            ))
          : [
              {
                title: 'Casos em andamento',
                value: data?.queue.cases.length ?? 0,
                caption:
                  `${pluralize(data?.queue.openCertificates ?? 0, 'certidão aberta', 'certidões abertas')} · ` +
                  `${data?.queue.blocked ?? 0} precisando de ação` +
                  (data?.queue.unconfirmed ? ` · ${data.queue.unconfirmed} sem vínculo confirmado` : ''),
                icon: 'tabler-folders',
                color: 'primary' as ThemeColor
              },
              {
                // t₄ — a única medição daqui que inclui estar errado. O resto
                // mede coisa que nós controlamos.
                title: 'Até o dossiê ser aceito (t₄)',
                value:
                  data?.acceptance.medianHoursToAccept === null || data?.acceptance.medianHoursToAccept === undefined
                    ? '—'
                    : `${data.acceptance.medianHoursToAccept} h`,
                caption:
                  `${data?.acceptance.accepted ?? 0} de ${data?.acceptance.reviewed ?? 0} respondido(s)` +
                  (data?.acceptance.withRework ? ` · ${data.acceptance.withRework} só depois de retrabalho` : ''),
                icon: 'tabler-checkup-list',
                color: 'success' as ThemeColor
              },
              {
                title: 'Tempo médio por certidão',
                value: formatMinutes(data?.timings.averageCycleMinutes),
                caption: `no portal: ${formatMinutes(data?.timings.averagePortalMinutes)}`,
                icon: 'tabler-clock',
                color: 'info' as ThemeColor
              },
              {
                title: 'Taxa de sucesso',
                value: formatPercent(data?.outcomes.successRate),
                caption: pluralize(data?.outcomes.attempts ?? 0, 'tentativa no período', 'tentativas no período'),
                icon: 'tabler-target-arrow',
                color: rateColor(data?.outcomes.successRate ?? null)
              },
              {
                title: 'Custo médio por caso',
                value: formatCents(data?.costs.averageCents ?? 0),
                caption: `${formatCents(data?.costs.totalCents ?? 0)} no período`,
                icon: 'tabler-cash',
                color: 'warning' as ThemeColor
              }
            ].map(kpi => (
              <Grid key={kpi.title} size={{ xs: 12, sm: 6, lg: 3 }}>
                <KpiCard {...kpi} />
              </Grid>
            ))}

        <Grid size={12}>
          <Card>
            <CardHeader
              title='Até o dossiê ser aceito (t₄), caso a caso'
              subheader='Do caso aberto ao primeiro aceite, com as recusas e o retrabalho até lá'
              action={
                <Button
                  size='small'
                  variant='tonal'
                  component='a'
                  href={`/files/operations/acceptance?days=${dias}`}
                  startIcon={<i className='tabler-file-spreadsheet' />}
                >
                  Exportar CSV
                </Button>
              }
            />
            <Divider />
            <div className='overflow-x-auto'>
              <TableDefault
                table={acceptanceTable}
                withPagination
                emptyMessage={isPending ? 'Carregando…' : 'Nenhum dossiê respondido no período.'}
              />
            </div>
          </Card>
        </Grid>

        <Grid size={12}>
          <Card>
            <CardHeader
              title='Fila de casos em andamento'
              subheader='Tudo que está aberto, sem recorte de período — o caso parado há meses é o que interessa'
            />
            <Divider />
            <div className='overflow-x-auto'>
              <TableDefault
                table={queueTable}
                withPagination
                emptyMessage={isPending ? 'Carregando…' : 'Nenhum caso em andamento.'}
              />
            </div>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <Card className='bs-full'>
            <CardHeader
              title='Taxa de sucesso por estratégia'
              subheader='Cada estratégia falha por um motivo diferente, e o conserto de cada uma é outro'
            />
            <Divider />
            <CardContent>
              {isPending ? <Skeleton variant='rounded' height={300} /> : <SuccessRateChart data={strategyData} />}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <Card className='bs-full'>
            <CardHeader
              title='Fila de vencimento'
              subheader={`Certidões de casos abertos vencendo em até ${data?.expiry.windowDays ?? 30} dias`}
              action={
                data?.expiry.expired ? (
                  <CustomChip
                    round='true'
                    size='small'
                    variant='tonal'
                    color='error'
                    label={pluralize(data.expiry.expired, 'vencida', 'vencidas')}
                  />
                ) : null
              }
            />
            <Divider />
            <CardContent>
              {isPending ? (
                <Skeleton variant='rounded' height={300} />
              ) : (
                <ExpiryQueueList certificates={data?.expiry.certificates ?? []} />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <Card className='bs-full'>
            <CardHeader
              title='Tempo médio por certidão'
              subheader='Ciclo é da abertura ao parecer; portal é o que a busca leva lá fora'
            />
            <Divider />
            <div className='overflow-x-auto'>
              <TableDefault
                table={timingTable}
                withPagination
                emptyMessage={isPending ? 'Carregando…' : 'Nenhuma certidão concluída no período.'}
              />
            </div>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <Card className='bs-full'>
            <CardHeader title='Taxa de sucesso por fonte' subheader='Pior primeiro' />
            <Divider />
            <div className='overflow-x-auto'>
              <TableDefault
                table={sourceTable}
                withPagination
                emptyMessage={isPending ? 'Carregando…' : 'Nenhuma tentativa registrada no período.'}
              />
            </div>
          </Card>
        </Grid>

        <Grid size={12}>
          <Card>
            <CardHeader
              title='Custo por caso'
              subheader='Ferramenta é captcha, bureau e IA; portal é emolumento e taxa — as consultas vão de R$ 0,20 a R$ 400'
            />
            <Divider />
            <CardContent className='flex flex-col gap-3'>
              {expensive.length ? (
                expensive.map(item => (
                  <div key={item.id} className='flex items-center justify-between gap-4'>
                    <Typography
                      variant='body2'
                      component={AppLink}
                      href={`/diligences/${item.id}`}
                      className='no-underline text-textPrimary hover:text-primary truncate'
                    >
                      {item.projectName}
                    </Typography>
                    <Typography variant='body2' color='text.secondary' className='whitespace-nowrap tabular-nums'>
                      {formatCents(item.toolCents)} + {formatCents(item.portalCents)} ={' '}
                      <strong className='text-textPrimary'>{formatCents(item.totalCents)}</strong>
                    </Typography>
                  </div>
                ))
              ) : (
                <Typography variant='body2' color='text.disabled'>
                  {isPending ? 'Carregando…' : 'Nenhum caso aberto no período.'}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  )
}
